import { IUseFormErrorBoundaryOptions, useFormErrorBoundary, useFieldActions, useFormState } from '@ngt/forms-core';
import { useEffect, useMemo, useState } from 'react';
import FormErrorDisplayMode from '@/types/form/FormErrorDisplayMode';
import { pascalToCameCasePropertyPath } from '@/utilities/pascalToCamelCase';
import { FormError } from '@/errors';
import FormErrorType from '@/types/form/FormErrorType';

export const errorSubscription = { touched: true, validating: true };


export const hideErrorFn: IUseFormErrorBoundaryOptions['includeError'] = () => false;
export const showErrorFn: IUseFormErrorBoundaryOptions['includeError'] = (name, error, { touched, validating }) => {
    return touched[name] && !validating
};
export const alwaysShowErrorFn: IUseFormErrorBoundaryOptions['includeError'] = () => true;

export const useFieldErrors = (errorMode?: FormErrorDisplayMode) => {
    const fieldActions = useFieldActions();
    const { validating } = useFormState({ validating: true });

    const includeErrorFn = useMemo(() => {

        switch (errorMode) {
            case FormErrorDisplayMode.AlwaysHideErrors:
                return hideErrorFn
            case FormErrorDisplayMode.AlwaysHideErrorMessages:
            case FormErrorDisplayMode.AlwaysShowAllErrors:
            case FormErrorDisplayMode.AlwaysShowFirstError:
                return alwaysShowErrorFn;
            case FormErrorDisplayMode.AutoHideErrorMessages:
            case FormErrorDisplayMode.AutoShowFirstError:
            case FormErrorDisplayMode.AutoShowAllErrors:
            default:
                return showErrorFn;
        }
    }, [])

    const data = useFormErrorBoundary<any, FormError>({
        subscription: errorSubscription,
        includeError: includeErrorFn
    });

    const output = useMemo(() => {
        if (!data || !fieldActions.getPropertyPath()) {
            return {
                hasErrors: false,
                errors: null,
                type: undefined
            };
        }

        const e = data.filter(x => pascalToCameCasePropertyPath(x.property) === fieldActions.getPropertyPath());

        if (e.length === 0) {
            return {
                hasErrors: false,
                errors: null,
                type: undefined
            };
        }

        switch (errorMode) {
            case FormErrorDisplayMode.AlwaysHideErrors:
                return {
                    hasErrors: false,
                    errors: null,
                    type: undefined
                };
            case FormErrorDisplayMode.AlwaysHideErrorMessages:
            case FormErrorDisplayMode.AutoHideErrorMessages:
                return {
                    hasErrors: true,
                    errors: null,
                    type: undefined
                };
            case FormErrorDisplayMode.AlwaysShowFirstError:
            case FormErrorDisplayMode.AutoShowFirstError:
                const fe = e.filter((x, i) => i === 0);

                return {
                    hasErrors: true,
                    errors: fe,
                    type: fe.reduce((a, b) => a >= (b.type ?? FormErrorType.Warning) ? a : b.type!, FormErrorType.Warning)
                };
            case FormErrorDisplayMode.AlwaysShowAllErrors:
            case FormErrorDisplayMode.AutoShowAllErrors:
            default:
                return {
                    hasErrors: true,
                    errors: e,
                    type: e.reduce((a, b) => a >= (b.type ?? FormErrorType.Warning) ? a : b.type!, FormErrorType.Warning)
                };
        }
    }, [data, fieldActions.getPropertyPath]);

    const [delayedOutput, setDelayedOutput] = useState(output);

    useEffect(() => {

        if (!validating) {
            setDelayedOutput(output);
        }
    }, [output, validating])

    return delayedOutput;
}

export default useFieldErrors;